/**
 * GitHub Integration API client.
 *
 * Manages the GitHub OAuth connection lifecycle:
 *   - Check connection status (Supabase query)
 *   - Initiate OAuth popup flow
 *   - Disconnect (delete row)
 */

import { supabase } from '@/lib/supabase'
import { isSchemaInfraError } from '@/lib/supabase-helpers'
import { getStudioSession } from '@/services/auth-provider'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GitHubConnection {
  connected: boolean
  username?: string
}

export interface GitHubSyncEntry {
  id: string
  commitSha: string
  commitMessage: string
  author: string
  filesChanged: string[]
  syncStatus: string
  createdAt: string
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export async function getGitHubStatus(userId: string): Promise<GitHubConnection> {
  const { data, error } = await supabase
    .schema('cogo')
    .from('github_connections')
    .select('github_username')
    .eq('user_id', userId)
    .maybeSingle()

  if (error) {
    if (isSchemaInfraError(error)) {
      console.warn(`[cogo-schema] ${error.code}: ${error.message} — returning disconnected`)
      return { connected: false }
    }
  }

  if (error || !data) {
    return { connected: false }
  }

  return { connected: true, username: data.github_username }
}

// ---------------------------------------------------------------------------
// Connect (popup flow)
// ---------------------------------------------------------------------------

export function connectGitHub(): Promise<string> {
  return new Promise((resolve, reject) => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? ''

    // Get the current session JWT to pass as `state`
    getStudioSession().then(({ data: { session } }) => {
      if (!session?.access_token) {
        reject(new Error('Not authenticated'))
        return
      }

      const connectUrl = `${supabaseUrl}/functions/v1/github-connect?state=${session.access_token}`
      const popup = window.open(connectUrl, 'github-connect', 'width=600,height=700')

      if (!popup) {
        reject(new Error('Popup blocked'))
        return
      }

      const onMessage = (event: MessageEvent) => {
        if (event.data?.type === 'github-connected') {
          window.removeEventListener('message', onMessage)
          resolve(event.data.username as string)
        }
      }

      window.addEventListener('message', onMessage)

      // Fallback: clean up if popup is closed without completing
      const poll = setInterval(() => {
        if (popup.closed) {
          clearInterval(poll)
          window.removeEventListener('message', onMessage)
          // Don't reject — user may have completed flow and popup auto-closed
        }
      }, 500)
    }).catch(reject)
  })
}

// ---------------------------------------------------------------------------
// Sync Log Queries
// ---------------------------------------------------------------------------

export async function getRecentSyncs(
  userId: string,
  limit = 5,
): Promise<GitHubSyncEntry[]> {
  const { data, error } = await supabase
    .schema('cogo')
    .from('github_sync_log')
    .select(
      `id, commit_sha, commit_message, author, files_changed, sync_status, created_at,
       connection:github_connections!inner(user_id)`,
    )
    .eq('connection.user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error) {
    if (isSchemaInfraError(error)) {
      console.warn(`[cogo-schema] ${error.code}: ${error.message} — returning empty syncs`)
      return []
    }
    console.error('[getRecentSyncs] Error:', error)
    return []
  }

  if (!data) return []

  return data.map((row: Record<string, unknown>) => ({
    id: row.id as string,
    commitSha: row.commit_sha as string,
    commitMessage: row.commit_message as string,
    author: row.author as string,
    filesChanged: row.files_changed as string[],
    syncStatus: row.sync_status as string,
    createdAt: row.created_at as string,
  }))
}

export async function hasUnseenSyncs(userId: string): Promise<boolean> {
  // Check for external_change_detected entries newer than last_exported_at
  const { data: conn, error: connErr } = await supabase
    .schema('cogo')
    .from('github_connections')
    .select('id, last_exported_at')
    .eq('user_id', userId)
    .maybeSingle()

  if (connErr && isSchemaInfraError(connErr)) {
    console.warn(`[cogo-schema] ${connErr.code}: ${connErr.message} — returning no unseen syncs`)
    return false
  }

  if (!conn) return false

  let query = supabase
    .schema('cogo')
    .from('github_sync_log')
    .select('id', { count: 'exact', head: true })
    .eq('connection_id', conn.id)
    .eq('sync_status', 'external_change_detected')

  if (conn.last_exported_at) {
    query = query.gt('created_at', conn.last_exported_at)
  }

  const { count, error: countErr } = await query
  if (countErr && isSchemaInfraError(countErr)) return false
  return (count ?? 0) > 0
}

// ---------------------------------------------------------------------------
// Export to GitHub
// ---------------------------------------------------------------------------

export interface ExportResult {
  ok: boolean
  repoUrl: string
  repoName: string
}

export interface ExportStatus {
  exported: boolean
  repoUrl?: string
  repoName?: string
  lastExportedAt?: string
}

export async function exportToGitHub(
  projectId: string,
  options?: { repoName?: string; isPrivate?: boolean },
): Promise<ExportResult> {
  const { data: { session } } = await getStudioSession()
  if (!session?.access_token) {
    throw new Error('Not authenticated')
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? ''
  const res = await fetch(`${supabaseUrl}/functions/v1/github-export`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      projectId,
      repoName: options?.repoName,
      isPrivate: options?.isPrivate,
    }),
  })

  const json = await res.json()
  if (!res.ok || !json.ok) {
    throw new Error(json.error || 'Export failed')
  }

  return { ok: true, repoUrl: json.repoUrl, repoName: json.repoName }
}

export async function getExportStatus(
  userId: string,
): Promise<ExportStatus | null> {
  const { data, error } = await supabase
    .schema('cogo')
    .from('github_connections')
    .select('repo_name, repo_url, last_exported_at')
    .eq('user_id', userId)
    .maybeSingle()

  if (error) {
    if (isSchemaInfraError(error)) {
      console.warn(`[cogo-schema] ${error.code}: ${error.message} — returning null export status`)
      return null
    }
  }

  if (error || !data) return null

  return {
    exported: !!data.repo_url,
    repoUrl: data.repo_url ?? undefined,
    repoName: data.repo_name ?? undefined,
    lastExportedAt: data.last_exported_at ?? undefined,
  }
}

// ---------------------------------------------------------------------------
// Disconnect
// ---------------------------------------------------------------------------

export async function disconnectGitHub(userId: string): Promise<void> {
  const { error } = await supabase
    .schema('cogo')
    .from('github_connections')
    .delete()
    .eq('user_id', userId)

  if (error) {
    if (isSchemaInfraError(error)) {
      console.warn(`[cogo-schema] ${error.code}: ${error.message} — disconnect skipped`)
      return
    }
    console.error('[disconnectGitHub] Error:', error)
    throw error
  }
}
